/**
 * Cookie Consent Banner
 *
 * Non-intrusive banner displayed at the bottom of the viewport when the user
 * has not yet made a consent choice. Provides quick actions to accept all,
 * reject non-essential, or open detailed preferences.
 *
 * Business Rules:
 * - Only shown when consent has not been set (first visit or version change)
 * - Hidden after any consent choice is made
 * - "Essential only" and "Accept all" have equal prominence (GDPR best practice)
 * - Mobile-friendly with stacked buttons on small screens
 *
 * Accessibility:
 * - Uses semantic section element with aria-label
 * - All buttons are keyboard accessible
 * - Sufficient color contrast
 */

import { Paper, Text, Button, Group, Stack, Box } from '@mantine/core';
import { IconCookie } from '@tabler/icons-react';
import { useConsent } from '../../contexts/ConsentContext';
import { brandColors, semanticColors } from '../../config/theme';
import { CONSENT_UI_TEXT } from '../../constants/consent';

/**
 * Cookie consent banner component.
 * Renders at the bottom of the viewport when consent is not set.
 */
export const CookieBanner: React.FC = () => {
  const { isConsentSet, setConsent, openPreferences } = useConsent();

  // Don't render if consent has already been given
  if (isConsentSet) {
    return null;
  }

  /**
   * Handle "Accept All" - enable analytics and marketing.
   */
  const handleAcceptAll = () => {
    setConsent({
      analytics: true,
      marketing: true,
    });
  };

  /**
   * Handle "Essential Only" - disable analytics and marketing.
   */
  const handleEssentialOnly = () => {
    setConsent({
      analytics: false,
      marketing: false,
    });
  };

  return (
    <Box
      component="section"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: 'var(--mantine-spacing-md)',
        pointerEvents: 'none',
      }}
    >
      <Paper
        shadow="lg"
        p="md"
        radius="md"
        withBorder
        style={{
          maxWidth: 600,
          margin: '0 auto',
          pointerEvents: 'auto',
          backgroundColor: 'var(--mantine-color-body)',
        }}
      >
        <Stack gap="md">
          {/* Header with icon and description */}
          <Group gap="sm" align="flex-start">
            <IconCookie
              size={24}
              style={{ color: 'var(--mantine-color-orange-6)', flexShrink: 0, marginTop: 2 }}
            />
            <Box style={{ flex: 1 }}>
              <Text fw={600} size="sm" mb={4}>
                {CONSENT_UI_TEXT.bannerTitle}
              </Text>
              <Text size="sm" c={semanticColors.text.secondary}>
                {CONSENT_UI_TEXT.bannerDescription}
              </Text>
            </Box>
          </Group>

          {/* Action buttons - stack on mobile, row on desktop */}
          <Group gap="sm" grow preventGrowOverflow={false}>
            <Button
              variant="light"
              color={brandColors.neutral}
              size="sm"
              onClick={handleEssentialOnly}
              style={{ minHeight: 44 }}
            >
              {CONSENT_UI_TEXT.essentialOnly}
            </Button>
            <Button
              variant="light"
              size="sm"
              onClick={openPreferences}
              style={{ minHeight: 44 }}
            >
              {CONSENT_UI_TEXT.preferences}
            </Button>
            <Button
              variant="filled"
              size="sm"
              onClick={handleAcceptAll}
              style={{ minHeight: 44 }}
            >
              {CONSENT_UI_TEXT.acceptAll}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Box>
  );
};
